import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isStakeEngineStudio, type StakeReport } from '@sr-test-wizard/shared';
import { useStakeEngineStore } from '../../state/stakeEngineStore';
import { useRunStore } from '../../state/runStore';
import { StakeChat } from './StakeChat';
import { ModeStatsTable } from './ModeStatsTable';
import { ScenariosTab } from './ScenariosTab';

function VerdictBanner({ report }: { report: StakeReport | null }) {
  if (!report || report.status === 'idle') return null;
  if (report.status === 'running') {
    return <div className="verdict warn"><strong>Verification running…</strong></div>;
  }
  if (report.status === 'error') {
    return (
      <div className="verdict bad">
        <strong>Verification failed</strong>
        {report.error ? <span className="small"> — {report.error}</span> : null}
      </div>
    );
  }
  const ov = report.overall;
  const cls = ov === true ? 'good' : ov === false ? 'bad' : 'warn';
  const txt =
    ov === true
      ? 'ALL CHECKS PASSED'
      : ov === false
        ? 'VERIFICATION FAILED'
        : 'RAN — set a declared RTP to reconcile';
  return (
    <div className={`verdict ${cls}`}>
      <strong>{txt}</strong>
      {report.updatedAt ? (
        <span className="small muted"> · {new Date(report.updatedAt).toLocaleString()}</span>
      ) : null}
    </div>
  );
}

function gateBadge(passed: boolean | null): { cls: string; label: string } {
  if (passed === true) return { cls: 'good', label: 'PASS' };
  if (passed === false) return { cls: 'bad', label: 'FAIL' };
  return { cls: 'muted', label: 'N/A' };
}

function MathGate({ report }: { report: StakeReport }) {
  const g = report.math;
  const badge = gateBadge(g.passed);
  const rows = g.rows;
  const roundsByMode = new Map(report.rounds.rows.map((r) => [r.mode, r]));
  const roundsRan = report.rounds.ran;
  const explain = useStakeEngineStore((s) => s.explain);
  const explainBusy = useStakeEngineStore((s) => s.explainBusy);
  const explainError = useStakeEngineStore((s) => s.explainError);
  const generateExplain = useStakeEngineStore((s) => s.generateExplain);

  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Math gate (analytic RTP)</h2>
        <span className="spacer" />
        {g.ran ? <span className="small muted">{g.seconds}s</span> : null}
        <span className={`small ${badge.cls}`}>{badge.label}</span>
      </div>
      {g.error ? (
        <p className="small" style={{ color: 'var(--bad)' }}>{g.error}</p>
      ) : rows.length === 0 ? (
        <p className="muted small" style={{ marginTop: 8 }}>Not run yet.</p>
      ) : (
        <>
          <p className="muted small" style={{ margin: '8px 0 0' }}>
            Click a mode to expand its full distribution &amp; statistics
            {roundsRan ? ' + round-type breakdown' : ''}.
          </p>
          <ModeStatsTable
            rows={rows}
            roundsByMode={roundsRan ? roundsByMode : undefined}
            explain={explain}
            explainBusy={explainBusy}
            explainError={explainError}
            onExplain={roundsRan ? generateExplain : undefined}
          />
        </>
      )}
    </section>
  );
}

function IntegrityGate({ report }: { report: StakeReport }) {
  const g = report.integrity;
  const badge = g.skipped ? { cls: 'muted', label: 'SKIPPED' } : gateBadge(g.passed);
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Integrity gate (books ↔ lookup)</h2>
        <span className="spacer" />
        {g.ran ? <span className="small muted">{g.seconds}s</span> : null}
        <span className={`small ${badge.cls}`}>{badge.label}</span>
      </div>
      {g.skipped ? (
        <p className="muted small" style={{ marginTop: 8 }}>
          Skipped. Enable "Run integrity gate" before verifying — it decompresses the book files
          and can be slow for large bonus modes.
        </p>
      ) : g.error ? (
        <p className="small" style={{ color: 'var(--bad)' }}>{g.error}</p>
      ) : g.rows.length === 0 ? (
        <p className="muted small" style={{ marginTop: 8 }}>Not run yet.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 8 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>mode</th><th>result</th><th>book records</th><th>lookup rows</th>
                <th>counts</th><th>payouts</th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map((r) => {
                const b = gateBadge(r.passed);
                return (
                  <tr key={r.mode}>
                    <td>
                      {r.mode}
                      {r.firstMismatch ? (
                        <div className="small muted">
                          first mismatch @ {r.firstMismatch.index}: book={r.firstMismatch.book} lookup={r.firstMismatch.lookup}
                        </div>
                      ) : null}
                      {r.error ? <div className="small" style={{ color: 'var(--bad)' }}>{r.error}</div> : null}
                    </td>
                    <td className={`small ${b.cls}`}>{b.label}</td>
                    <td>{r.bookRecords.toLocaleString()}</td>
                    <td>{r.lookupRows.toLocaleString()}</td>
                    <td className={`small ${r.countMatch ? 'good' : 'bad'}`}>{r.countMatch ? 'match' : 'differ'}</td>
                    <td className={`small ${r.payoutsMatch ? 'good' : 'bad'}`}>{r.payoutsMatch ? 'match' : 'differ'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function FairGate({ report }: { report: StakeReport }) {
  const g = report.fair;
  const badge = gateBadge(g.passed);
  const s = g.summary;
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Provably-fair gate (RNG)</h2>
        <span className="spacer" />
        {g.ran ? <span className="small muted">{g.seconds}s</span> : null}
        <span className={`small ${badge.cls}`}>{badge.label}</span>
      </div>
      {g.error ? (
        <p className="small" style={{ color: 'var(--bad)' }}>{g.error}</p>
      ) : s ? (
        <div style={{ marginTop: 8 }}>
          <div className="kv-row small">
            <span>cases: <strong>{s.total.toLocaleString()}</strong></span>
            <span>rand match: <strong>{s.randOk.toLocaleString()} / {s.total.toLocaleString()}</strong></span>
            <span>roll match: <strong>{s.rollOk.toLocaleString()} / {s.total.toLocaleString()}</strong></span>
          </div>
          <p className="muted small" style={{ marginTop: 6 }}>
            Reproduces vendor HMAC-SHA256 entropy and uint128-mod rolls from server/client seed + nonce.
          </p>
        </div>
      ) : (
        <p className="muted small" style={{ marginTop: 8 }}>Not run yet.</p>
      )}
    </section>
  );
}

/**
 * Extras "Stake Engine" page (EAS only): run the independent verifier (math /
 * integrity / provably-fair gates) against the pulled submission package and
 * chat about the results.
 */
export function StakeEnginePage() {
  const { id } = useParams<{ id: string }>();
  const runId = id ?? '';
  const load = useStakeEngineStore((s) => s.load);
  const verify = useStakeEngineStore((s) => s.verify);
  const report = useStakeEngineStore((s) => s.report);
  const progress = useStakeEngineStore((s) => s.progress);
  const error = useStakeEngineStore((s) => s.error);
  const studio = useRunStore((s) => s.current?.studio ?? null);

  const [declaredRtp, setDeclaredRtp] = useState('97');
  const [doIntegrity, setDoIntegrity] = useState(false);
  const [doRoundTypes, setDoRoundTypes] = useState(false);
  const [view, setView] = useState<'rtp' | 'scenarios'>('rtp');

  useEffect(() => {
    if (runId) void load(runId);
  }, [runId, load]);

  const verifyProgress = progress.verify;
  const running =
    report?.status === 'running' ||
    verifyProgress?.status === 'started' ||
    verifyProgress?.status === 'progress';

  const notEas = studio !== null && !isStakeEngineStudio(studio);

  const runVerify = () => {
    const parsed = declaredRtp.trim() === '' ? null : Number(declaredRtp);
    void verify({
      doIntegrity,
      doRoundTypes,
      declaredRtp: parsed !== null && Number.isFinite(parsed) ? parsed : null,
    });
  };

  return (
    <div className="stake-engine-page">
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Stake Engine Verification</h1>
        <p className="muted">
          Independent verification of an EasyGo Games (EAS) Stake Engine submission: analytic RTP &
          volatility, books↔lookup integrity, and provably-fair RNG reproduction. Runs against the
          source pulled in Phase 3.
        </p>
      </header>

      {notEas && (
        <div className="verdict warn" style={{ marginBottom: 16 }}>
          <strong>Not an EasyGo Games (EAS) run.</strong>
          <span className="small"> This mode is intended for Stake Engine submissions.</span>
        </div>
      )}

      <div className="tab-bar" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={`tab${view === 'rtp' ? ' active' : ''}`}
          onClick={() => setView('rtp')}
        >
          RTP analysis
        </button>
        <button
          type="button"
          className={`tab${view === 'scenarios' ? ' active' : ''}`}
          onClick={() => setView('scenarios')}
        >
          Scenarios / Debug
        </button>
      </div>

      {view === 'scenarios' ? (
        <ScenariosTab runId={runId} report={report} />
      ) : (
        <>
      {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label className="small muted" style={{ display: 'block' }}>Declared RTP % (reconcile)</label>
            <input
              type="number"
              step="0.0001"
              value={declaredRtp}
              onChange={(e) => setDeclaredRtp(e.target.value)}
              style={{ width: 140 }}
              placeholder="97"
            />
          </div>
          <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={doIntegrity}
              onChange={(e) => setDoIntegrity(e.target.checked)}
            />
            Run integrity gate (decompresses book files — slower)
          </label>
          <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={doRoundTypes}
              onChange={(e) => setDoRoundTypes(e.target.checked)}
            />
            Run round-type analysis (decompresses book files — slower)
          </label>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-primary"
            disabled={running || notEas}
            onClick={runVerify}
          >
            {running ? 'Verifying…' : report?.status === 'ready' ? 'Re-run verification' : 'Run verification'}
          </button>
        </div>
        {running && (
          <p className="small muted" style={{ margin: '8px 0 0' }}>
            {verifyProgress?.tool
              ? `Running… ${verifyProgress.tool}`
              : verifyProgress?.message || 'Working…'}
          </p>
        )}
        {verifyProgress?.status === 'failed' && (
          <p className="small" style={{ color: 'var(--bad)', margin: '8px 0 0' }}>
            Failed{verifyProgress.message ? `: ${verifyProgress.message}` : ''}
          </p>
        )}
      </section>

      <VerdictBanner report={report} />

      {report && report.status !== 'idle' && (
        <>
          <MathGate report={report} />
          <IntegrityGate report={report} />
          <FairGate report={report} />
        </>
      )}

      <section className="card">
        <StakeChat runId={runId} />
      </section>
        </>
      )}
    </div>
  );
}
